const DEFAULT_DURATION = 600;
const EASING = [0.22, 1, 0.36, 1];

// cubic-bezier() maps progress to progress, so x has to be solved for the
// elapsed fraction before y can be read off.
const bezierAt = (first, second, ratio) =>
  3 * (1 - ratio) * (1 - ratio) * ratio * first + 3 * (1 - ratio) * ratio * ratio * second + ratio * ratio * ratio;

const bezierSlopeAt = (first, second, ratio) =>
  3 * (1 - ratio) * (1 - ratio) * first + 6 * (1 - ratio) * ratio * (second - first) + 3 * ratio * ratio * (1 - second);

function solveEasing(firstX, firstY, secondX, secondY) {
  return (elapsedRatio) => {
    let ratio = elapsedRatio;
    for (let iteration = 0; iteration < 5; iteration += 1) {
      const slope = bezierSlopeAt(firstX, secondX, ratio);
      if (slope === 0) break;
      ratio -= (bezierAt(firstX, secondX, ratio) - elapsedRatio) / slope;
    }
    return bezierAt(firstY, secondY, ratio);
  };
}

const ease = solveEasing(...EASING);

// A wrapper, not a customised <a>: Safari has no customised built-ins.
class SmoothAnchor extends HTMLElement {
  connectedCallback() {
    this.link = this.querySelector('a[href*="#"]');
    if (!this.link) return;

    this.link.addEventListener('click', (event) => this.handleClick(event));
  }

  handleClick(event) {
    // Modified clicks keep their native meaning — new tab, new window.
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    // A link that leaves this page has to navigate as usual.
    if (this.link.pathname !== window.location.pathname) return;

    const target = document.getElementById(this.link.hash.slice(1));
    if (!target) return;

    event.preventDefault();
    this.animateTo(target);
    // replaceState, not location.hash: assigning the hash jumps the page.
    window.history.replaceState(null, '', this.link.hash);
  }

  // The sticky header's height is not a constant, so it is read from the
  // custom property the header maintains; data-offset stays as a manual override.
  resolveOffset() {
    if (this.dataset.offset) return Number(this.dataset.offset) || 0;

    const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height'));
    return Number.isFinite(headerHeight) ? headerHeight : 0;
  }

  animateTo(target) {
    const offset = this.resolveOffset();
    const duration = Number(this.dataset.duration) || DEFAULT_DURATION;
    const start = window.scrollY;
    const furthest = document.documentElement.scrollHeight - window.innerHeight;
    const destination = Math.max(0, Math.min(start + target.getBoundingClientRect().top - offset, furthest));

    // A native anchor jump takes focus with it; keyboard and screen reader users
    // would otherwise stay behind at the button.
    const settle = () => {
      target.setAttribute('tabindex', '-1');
      target.focus({ preventScroll: true });
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.scrollTo({ top: destination, behavior: 'instant' });
      settle();
      return;
    }

    const startedAt = performance.now();
    const step = (now) => {
      const elapsed = Math.min((now - startedAt) / duration, 1);
      // instant: a stray scroll-behavior: smooth would re-animate every frame.
      window.scrollTo({ top: start + (destination - start) * ease(elapsed), behavior: 'instant' });

      if (elapsed < 1) requestAnimationFrame(step);
      else settle();
    };

    requestAnimationFrame(step);
  }
}

// Two sections load this file, so the second run must not redefine the element.
if (!customElements.get('smooth-anchor')) {
  customElements.define('smooth-anchor', SmoothAnchor);
}
